import { isRecord } from '@/lib/api'

const ABOVE_SCOPE = 'another entity'
const EFFECTIVE_CHANGE = 'activity.setting.effective-changed'

export type DetailRow = { property: string; before?: string; after?: string }

export type ChangeDetail = {
  messageKey: string
  params: Record<string, string>
}

export function parseChangeDetail(text: string): ChangeDetail | null {
  const raw = parseValue(text)
  if (!isRecord(raw) || typeof raw.messageKey !== 'string') return null
  const params: Record<string, string> = {}
  if (isRecord(raw.params)) {
    for (const [key, value] of Object.entries(raw.params))
      if (typeof value === 'string') params[key] = value
  }
  return { messageKey: raw.messageKey, params }
}

export function changeLocation(
  detail: ChangeDetail,
  source: string,
  sourceEntity: string | null,
): string | null {
  if (source === 'inherited') return `Inherited from ${sourceEntity ?? ABOVE_SCOPE}`
  return detail.messageKey !== EFFECTIVE_CHANGE || detail.params.sourceEntity !== undefined
    ? 'Changed here'
    : null
}

export function deriveRows(
  params: Record<string, string>,
  labelFor: (property: string) => string,
  formatValue: (value: unknown) => string = scalarText,
): DetailRow[] {
  const oldRaw = params.old
  const newRaw = params.new
  if (oldRaw === undefined && newRaw === undefined) return []

  const oldValue = oldRaw === undefined ? undefined : parseValue(oldRaw)
  const newValue = newRaw === undefined ? undefined : parseValue(newRaw)

  if (isRecord(oldValue) || isRecord(newValue)) {
    const before = isRecord(oldValue) ? oldValue : {}
    const after = isRecord(newValue) ? newValue : {}
    return [...new Set([...Object.keys(before), ...Object.keys(after)])]
      .filter((k) => JSON.stringify(before[k]) !== JSON.stringify(after[k]))
      .map((k) => ({
        property: labelFor(k),
        before: k in before ? formatValue(before[k]) : undefined,
        after: k in after ? formatValue(after[k]) : undefined,
      }))
  }

  return [
    {
      property: 'Value',
      before: oldValue === undefined ? undefined : formatValue(oldValue),
      after: newValue === undefined ? undefined : formatValue(newValue),
    },
  ]
}

export function settingLabel(key: string): string {
  return humanizeKey(key)
}

export function propertyLabel(property: string): string {
  return humanizeKey(property)
}

function humanizeKey(key: string): string {
  const words = key
    .replace(/[._-]/g, ' ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

function parseValue(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

function scalarText(value: unknown): string {
  return typeof value === 'object' && value !== null
    ? JSON.stringify(value)
    : String(value)
}
